import { definePlugin } from '../../framework';
import type { FieldSuggestion, TransformState, TransformStep } from '../../types';
import {
  acceptedValues,
  confirmingTextsFor,
  fieldExpectation,
  type FieldExpectation,
} from '../../utils/confirmation';
import { unwrapSnippet } from '../../utils/snippets';

// Longer values are sentences, not a label around a value.
const MAX_WORDS = 12;

const words = (text: string) => text.trim().split(/\s+/).filter(Boolean);

/** The editor's Remove Words block: drops words from the start and the end. */
export const buildRemoveWordsSnippet = (snippet: unknown, fromStart: number, fromEnd: number) => ({
  '$helpers.removeWords': [[[unwrapSnippet(snippet)]], fromStart, fromEnd],
});

/**
 * Where a run of words sits in a value: how many words come before it and
 * after it. Null when it isn't there as whole words.
 */
export const findWordRun = (
  valueWords: string[],
  runWords: string[],
): { fromStart: number; fromEnd: number } | null => {
  for (let i = 0; i + runWords.length <= valueWords.length; i++) {
    if (runWords.every((word, j) => valueWords[i + j] === word)) {
      return { fromStart: i, fromEnd: valueWords.length - i - runWords.length };
    }
  }
  return null;
};

/**
 * The value with words removed from its start and end, for each confirmed
 * text that is a run of two or more words in it ("for the carrier HMM
 * Co.,Ltd." → "HMM Co.,Ltd."). A single word is a part Split Handling
 * already offers.
 */
export const removeWordsCandidates = (
  state: TransformState,
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  field: FieldExpectation,
  enumValues: string[] = [],
): TransformState[] => {
  const valueWords = typeof state.value === 'string' ? words(state.value) : [];
  if (valueWords.length < 3 || valueWords.length > MAX_WORDS) return [];

  return confirmingTextsFor(state, suggestion, others, field, enumValues).flatMap((text) => {
    const runWords = words(text);
    if (runWords.length < 2) return [];
    const run = findWordRun(valueWords, runWords);
    if (run === null) return [];
    return [
      {
        snippet: buildRemoveWordsSnippet(state.snippet, run.fromStart, run.fromEnd),
        value: runWords.join(' '),
      },
    ];
  });
};

export const removeWordsStep: TransformStep = {
  name: 'removeWords',
  stage: 'split',
  mode: 'add',
  apply: (state, { entry, suggestion, suggestions }) =>
    removeWordsCandidates(state, suggestion, suggestions, fieldExpectation(entry), acceptedValues(entry)),
};

export const removeWords = definePlugin<{}>({
  name: 'Remove Words',
  version: '0.1.0',
  description:
    'Replaces the Remove Words transformer: removes words from the start and end of a value when something confirms what is left.',
  initialState: {},

  bootstrap: () => {},

  transformSteps: () => [removeWordsStep],
});
